import React from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Card } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { useOpenAIModels } from '@/hooks/useOpenAIModels';

export interface ChatSettingsConfig {
  model: string;
  maxTokens: number;
  reasoningEffort?: 'low' | 'medium' | 'high';
}

interface ChatSettingsProps {
  settings: ChatSettingsConfig;
  onSettingsChange: (settings: ChatSettingsConfig) => void;
}

const ChatSettings: React.FC<ChatSettingsProps> = ({ settings, onSettingsChange }) => {
  const { models, isLoading } = useOpenAIModels();
  
  const selectedModel = models.find(m => m.value === settings.model);
  const supportsReasoning = selectedModel?.supportsReasoning || false;

  return (
    <Card className="p-4 space-y-4 border-border bg-card">
      <div className="space-y-2">
        <Label htmlFor="model" className="text-sm font-medium text-foreground">
          Model
        </Label>
        <Select
          value={settings.model}
          onValueChange={(value) => onSettingsChange({ ...settings, model: value })}
          disabled={isLoading}
        >
          <SelectTrigger id="model" className="bg-background">
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading models...</span>
              </div>
            ) : (
              <SelectValue />
            )}
          </SelectTrigger>
          <SelectContent>
            {models.map((model) => (
              <SelectItem key={model.value} value={model.value}>
                {model.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="maxTokens" className="text-sm font-medium text-foreground">
          Max Response Tokens: {settings.maxTokens}
        </Label>
        <Slider
          id="maxTokens"
          min={100}
          max={50000}
          step={100}
          value={[settings.maxTokens]}
          onValueChange={([value]) => onSettingsChange({ ...settings, maxTokens: value })}
          className="w-full"
        />
        <p className="text-xs text-muted-foreground">
          Limit the length of responses to control costs
        </p>
      </div>

      {supportsReasoning && (
        <div className="space-y-2">
          <Label htmlFor="reasoning" className="text-sm font-medium text-foreground">
            Reasoning Effort
          </Label>
          <Select
            value={settings.reasoningEffort || 'medium'}
            onValueChange={(value: 'low' | 'medium' | 'high') => 
              onSettingsChange({ ...settings, reasoningEffort: value })
            }
          >
            <SelectTrigger id="reasoning" className="bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low (Faster)</SelectItem>
              <SelectItem value="medium">Medium (Balanced)</SelectItem>
              <SelectItem value="high">High (Thorough)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Higher effort = more thorough reasoning but slower responses
          </p>
        </div>
      )}
    </Card>
  );
};

export default ChatSettings;
